import { useState } from "react";
import { Plus, Settings2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export function CategoryManager({ categories, onSaved }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const start = () => { setRows(categories.map((c) => ({ orig: c, name: c }))); setOpen(true); };
  const set = (i, name) => setRows((r) => r.map((x, j) => (j === i ? { ...x, name } : x)));

  const save = async () => {
    setSaving(true);
    try {
      const renames = Object.fromEntries(rows.filter((r) => r.orig && r.name.trim() && r.orig !== r.name.trim()).map((r) => [r.orig, r.name.trim()]));
      const { data } = await api.put("/photo-categories", { categories: rows.map((r) => r.name), renames });
      onSaved(data.categories);
      toast.success("Photo categories saved");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save categories");
    } finally { setSaving(false); }
  };

  return (
    <>
      <Button size="sm" variant="ghost" onClick={start} className="h-9 rounded-full" data-testid="photo-categories-manage"><Settings2 className="w-4 h-4 mr-1.5" />Categories</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="photo-categories-dialog">
          <DialogHeader>
            <DialogTitle>Photo categories</DialogTitle>
            <DialogDescription>The AI sorts every photo into one of these. Photos it isn't sure about go to Unsorted. Removing a category moves its photos to Unsorted.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
            {rows.map((r, i) => (
              <div key={i} className="flex gap-2">
                <input value={r.name} onChange={(e) => set(i, e.target.value)} maxLength={40} data-testid={`photo-category-input-${i}`}
                  className="flex-1 h-10 rounded-md border border-border bg-background px-3 text-sm" />
                <button type="button" onClick={() => setRows((x) => x.filter((_, j) => j !== i))} aria-label={`Remove ${r.name}`} data-testid={`photo-category-remove-${i}`}
                  className="w-10 h-10 rounded-md border border-border flex items-center justify-center hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <div className="flex justify-between gap-2 pt-2">
            <Button variant="outline" onClick={() => setRows((x) => [...x, { orig: null, name: "" }])} disabled={rows.length >= 30} data-testid="photo-category-add"><Plus className="w-4 h-4 mr-1.5" />Add category</Button>
            <Button onClick={save} disabled={saving} className="bg-[#FF5F15] hover:bg-[#E65100] text-white" data-testid="photo-categories-save">{saving ? "Saving…" : "Save"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
